import { Col, ColProps, Form, FormInstance, FormValues, Input, Row, Select } from 'antd';
import clsx from 'clsx';
import { isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import DatePicker from '../../../../../shared/components/date-picker/date-picker';
import { CheckIcon } from '../../../../../shared/icons/check-icon';
import { XIcon } from '../../../../../shared/icons/x-icon';
import ClientUsersSelect from '../../../../client-users/components/client-users-select/client-users-select';
import ClientUsersUnitSelect from '../../../../client-users/components/client-users-unit-select/client-users-unit-select';
import CustomersSelect from '../../../../customers/components/customers-select/customers-select';
import { TemplateField } from '../../../../templates/models/template-field';
import { signRequestFilterFormId } from '../../../constants/constants';
import { SignRequestFilterFormModel } from '../../../models/sign-request-filter-form-model';
import { SignRequestStatus } from '../../../models/sign-request-status';
import styles from './sign-requests-header-filter-form.module.css';
import { useGetUserQuery, useGetUserSettingsQuery } from '../../../../user/api/user-api';
import { UserPermissionType } from '../../../../user/models/user-permission-type';
import { UserSettings } from '../../../../user/models/user-settings';
import { hasPermission } from '../../../../../shared/utils/has-permission';
import { parseServerDateFormat } from '../../../../../shared/utils/format-date/format-date';

interface Props {
  form: FormInstance<SignRequestFilterFormModel>;
  visibleTemplateFields?: boolean;
  templateFields?: TemplateField[];
  initialValues: FormValues<SignRequestFilterFormModel>;
}

const SignRequestsHeaderFilterForm = ({
  form,
  visibleTemplateFields,
  templateFields,
  initialValues,
}: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'sign-requests']);
  const columnsGrid: ColProps = { xs: 24, sm: 12, md: 6 };
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: currentUser } = useGetUserQuery();
  const [searchParams] = useSearchParams();

  const settingName = UserSettings.SignRequestsTableColumns + (searchParams.get('template') || '');
  const visibleColumnsKeys = userSettings?.[settingName]?.split(',') || [];

  const isColumnVisible = (columnKey: string): boolean => {
    if (!visibleColumnsKeys.length) return true;
    return visibleColumnsKeys.includes(columnKey);
  };

  return (
    <Form initialValues={initialValues} id={signRequestFilterFormId} form={form} layout="vertical">
      <Row gutter={18} align="top">
        <Col {...columnsGrid}>
          <Form.Item name="dateFromTo" label={t('signScanRequest.label.date')}>
            <DatePicker.RangePicker
              className={styles.input}
              disabledDate={date => isAfter(date, new Date())}
              format={parseServerDateFormat(userSettings?.[UserSettings.DateFormat])}
            />
          </Form.Item>
        </Col>
        {isColumnVisible('name') && (
          <Col {...columnsGrid}>
            <Form.Item
              name="name"
              label={userSettings?.[UserSettings.SignRequestNameTitle] || t('label.name')}
              rules={[
                {
                  max: 255,
                  message: t('errors.nameTooLong'),
                },
              ]}
            >
              <Input placeholder={userSettings?.[UserSettings.SignRequestNameTitle] || t('label.name')} />
            </Form.Item>
          </Col>
        )}
        {isColumnVisible('user') && (
          <Col {...columnsGrid}>
            <Form.Item name="clientUser" label={t('label.user')}>
              <ClientUsersSelect placeholder={t('label.user')} />
            </Form.Item>
          </Col>
        )}
        {isColumnVisible('unit') && (
          <Col {...columnsGrid}>
            <Form.Item
              name="unit"
              rules={[
                {
                  max: 255,
                  message: t('signScanRequest.validationError.unitTooLong'),
                },
              ]}
              label={t('label.unit')}
            >
              <ClientUsersUnitSelect />
            </Form.Item>
          </Col>
        )}
        {isColumnVisible('customer') && currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers) && (
          <Col {...columnsGrid}>
            <Form.Item name="customerId" label={t('label.customer')}>
              <CustomersSelect placeholder={t('signScanRequest.placeholder.customer')} />
            </Form.Item>
          </Col>
        )}
        {isColumnVisible('archived') && (
          <Col {...columnsGrid}>
            <Form.Item name="archived" label={t('signScanRequest.label.uploadToServer')}>
              <Select
                defaultActiveFirstOption={false}
                allowClear={true}
                options={[
                  {
                    value: '0',
                    label: t('signScanRequest.label.archiveOptions.onlyUnarchived'),
                  },
                  {
                    value: '1',
                    label: t('signScanRequest.label.archiveOptions.onlyArchived'),
                  },
                ]}
              />
            </Form.Item>
          </Col>
        )}
        {isColumnVisible('status') && (
          <Col {...columnsGrid}>
            <Form.Item name="status" label={t('label.status')}>
              <Select
                allowClear={true}
                options={[
                  {
                    value: SignRequestStatus.Signed,
                    label: t('signRequestsFilterForm.label.signed', { ns: 'sign-requests' }),
                  },
                  {
                    value: SignRequestStatus.Pending,
                    label: t('signRequestsFilterForm.label.pending', { ns: 'sign-requests' }),
                  },
                ]}
              />
            </Form.Item>
          </Col>
        )}
        {templateFields &&
          templateFields.map(
            (field, index) =>
              isColumnVisible(field.name) && (
                <Col
                  {...columnsGrid}
                  key={index}
                  className={clsx(
                    index > 0 && styles.templateField,
                    !visibleTemplateFields && styles.visibleTemplateFields,
                  )}
                >
                  <Form.Item label={field.label} name={field.name}>
                    {field.type === 'boolean' ? (
                      <Select
                        defaultActiveFirstOption={false}
                        allowClear={true}
                        options={[
                          {
                            value: 'true',
                            label: <CheckIcon />,
                          },
                          {
                            value: 'false',
                            label: <XIcon />,
                          },
                        ]}
                      />
                    ) : field.type === 'date' ? (
                      <DatePicker
                        className={styles.input}
                        format={parseServerDateFormat(userSettings?.[UserSettings.DateFormat])}
                      />
                    ) : (
                      <Input />
                    )}
                  </Form.Item>
                </Col>
              ),
          )}
      </Row>
    </Form>
  );
};

export default SignRequestsHeaderFilterForm;
